import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BrandType } from "entities/brands/types";
import {
  moduleLayoutOptionsWithoutIcon,
  moduleTypeOptionsWithoutIcon,
} from "entities/modules/constants";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import {
  ContentBlocksModuleLayout,
  ModuleType,
  PromotionModuleLayout,
} from "entities/modules/types";
import React, { ChangeEvent, FC, useMemo } from "react";
import { useParams } from "react-router-dom";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import SchedulingPicker from "shared/components/Form/SchedulingPicker";
import Select from "shared/components/Form/Select";
import Switch from "shared/components/Form/Switch";
import TextField from "shared/components/Form/TextField";
import {
  COLOR_BLACK,
  COLOR_GREY_6,
  COLOR_GREY_8,
  COLOR_PRIMARY_MAIN,
} from "shared/theme/colors";
import { useGeneralData } from "../../../../stores/useGeneralData";
import { useStudiosData } from "../../../../stores/useStudiosData";
import {
  moduleConfigurationView,
  moduleIcons,
  moduleTypeOptions,
} from "./constants";
import { IconListContainer, IconWrapper } from "./styled";
import { assureValidLayout } from "./utils";

const General: FC = () => {
  const { id: brandId } = useParams();
  const {
    moduleName,
    moduleType,
    adminNotes,
    moduleLayout,
    studioFilterEnabled,
    iconShown,
    icon,
    scheduledStartAt,
    scheduledEndAt,
    isSchedulingEnabled,
    setModuleName,
    setModuleType,
    setAdminNotes,
    setModuleLayout,
    setBonusObtainType,
    setIconShown,
    setIcon,
    setScheduledStartAt,
    setScheduledEndAt,
    setIsSchedulingEnabled,
  } = useGeneralData();

  const setTagBlocks = useTagBlocksStore((state) => state.setTagBlocks);
  const setSelectedStudios = useStudiosData(
    (state) => state.setSelectedStudios,
  );

  const filteredModuleTypeOptions = useMemo(() => {
    if (brandId === BrandType.FREE) {
      return moduleTypeOptions.filter(
        (option) =>
          option.value === ModuleType.Games ||
          option.value === ModuleType.ContentBlocks,
      );
    }
    return moduleTypeOptions;
  }, [brandId]);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setModuleName(e.target.value);
  };

  const handleTypeChange = (value: string) => {
    setModuleType(value as ModuleType);
    assureValidLayout({
      moduleType: value as ModuleType,
      studioFilterEnabled,
      moduleLayout,
      setModuleLayout,
    });
    setTagBlocks([]);
    setSelectedStudios([]);
    setBonusObtainType(null);
  };

  const handleAdminNotesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAdminNotes(e.target.value);
  };

  const handleIconShownChange = (checked: boolean) => {
    setIconShown(checked);
    if (!checked) {
      setIcon(null);
    }
  };

  const allowIconSelection =
    moduleType &&
    !moduleTypeOptionsWithoutIcon.includes(moduleType) &&
    !moduleLayoutOptionsWithoutIcon.includes(
      moduleLayout as ContentBlocksModuleLayout | PromotionModuleLayout,
    );

  return (
    <Stack spacing="1rem">
      <ContentSection>
        <SchedulingPicker
          scheduledStartAt={scheduledStartAt || ""}
          onScheduledStartAtChange={setScheduledStartAt}
          scheduledEndAt={scheduledEndAt || ""}
          onScheduledEndAtChange={setScheduledEndAt}
          isSchedulingEnabled={isSchedulingEnabled || false}
          onSchedulingEnabledChange={setIsSchedulingEnabled}
          title="Scheduling"
          showToggle={true}
          showEndDate={true}
          dateLabel="Start Date"
          endDateLabel="End Date"
        />
      </ContentSection>

      <ContentSection title="GENERAL">
        <Stack spacing="1rem">
          <TextField
            id="moduleName"
            label="Name"
            labelColor={COLOR_BLACK}
            placeholder="Enter module name"
            fullWidth
            required
            value={moduleName}
            onChange={handleNameChange}
          />
          <Select
            id="moduleType"
            label="Type"
            labelColor={COLOR_BLACK}
            placeholder="Select a type"
            disabledPlaceholder
            fullWidth
            required
            value={moduleType ?? undefined}
            handleChange={handleTypeChange}
            options={filteredModuleTypeOptions}
          />
          <TextField
            id="adminNotes"
            label="Admin Notes"
            labelColor={COLOR_BLACK}
            placeholder="Enter notes"
            fullWidth
            value={adminNotes}
            onChange={handleAdminNotesChange}
          />
          {allowIconSelection && (
            <Stack spacing="0.5rem">
              <Switch
                label="Show icon"
                value={iconShown}
                onChange={handleIconShownChange}
              />
              {iconShown && (
                <>
                  <IconListContainer>
                    {moduleIcons.map(({ name, Component }) => (
                      <IconWrapper key={name} onClick={() => setIcon(name)}>
                        <Component
                          color={
                            icon === name ? COLOR_PRIMARY_MAIN : COLOR_GREY_6
                          }
                        />
                      </IconWrapper>
                    ))}
                  </IconListContainer>
                  <Typography color={COLOR_GREY_8} variant="bodyMRegular">
                    Select an icon to display next to the name
                  </Typography>
                </>
              )}
            </Stack>
          )}
        </Stack>
      </ContentSection>
      {moduleType ? moduleConfigurationView[moduleType] : null}
    </Stack>
  );
};

export default General;
